import * as Sentry from "@sentry/browser";
import type { FC, ReactElement } from "react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Loading } from "components";

import type { GetCurrentUserQuery, UserType } from "api/generated";
import { useGetCurrentUserQuery, useSetTimetableGenericMutation } from "api/generated";

import { useAppDispatch } from "store/hooks";
import { setTimetableCreatedDate, setUser } from "store/slices/user";

import { formatDate } from "utils/calendarDateFormatting";
import { getCalendarRange } from "utils/calendarRange";
import { preloadCalendarRouteWhenIdle } from "utils/calendarRoute";
import { prefetchCalendarData } from "utils/hooks/usePrefetchCalendar";
import { processApiError } from "utils/processApiError";

import { PushNotificationOptIn } from "components/PushNotificationOptIn";
import { DEFAULT_GENERIC_TIMETABLE } from "const";
import BgWaveTop from "images/bg/bckg-branded-top.svg";
import { USER_REFRESH_AFTER_MS } from "./userRefresh";
import { startForegroundMessaging, stopForegroundMessaging } from "../../push/notificationService";

// AIDEV-NOTE: Lazy schválně — komponenta renderuje `null` a widget staví až v `useEffect`,
// takže odklad není vidět, ale `@sentry-internal/feedback` (48 kB) se tím dostane ze
// vstupního chunku, který stahuje každý student na každé stránce.
const FeedbackForm = lazy(async () => await import("components/FeedbackForm/FeedbackForm"));

interface Props {
  children: ReactElement;
}

type TCurrentUser = NonNullable<GetCurrentUserQuery["currentUser"]>;

// Krok, ve kterém student s nedokončeným onboardingem pokračuje.
const getOnboardingPath = (user: TCurrentUser) => {
  if (user.phoneValidationStatus === "accepted") return "/onboarding/priklad-uvod";

  if (typeof user.preferredSubject === "string" || user.phoneValidationStatus === "refused") {
    // With SMS switched off on the backend no code would arrive, so verification is skipped.
    return user.phoneValidationRequired ? "/onboarding/overeni-telefonu" : "/onboarding/priklad-uvod";
  }

  return user.onboarding.introduced ? "/onboarding/preference" : "/onboarding/uvod/1";
};

const AuthenticatedArea: FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: userData,
    isError: isUserError,
    fulfilledTimeStamp: userFulfilledTimeStamp,
    error: userError,
    refetch: refetchUser,
  } = useGetCurrentUserQuery();
  const [setTimetableGenericMutation, { error: apiMutationError }] = useSetTimetableGenericMutation();

  const [isLoading, setIsLoading] = useState(true);
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const userRefreshRef = useRef({ userFulfilledTimeStamp, refetchUser });
  userRefreshRef.current = { userFulfilledTimeStamp, refetchUser };
  const isFirstLoadHandledRef = useRef(false);
  const isCreatingTimetableRef = useRef(false);

  useEffect(() => {
    if (userError === undefined) return;

    processApiError(userError);
  }, [userError]);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processApiError(apiMutationError);
  }, [apiMutationError]);

  // AIDEV-NOTE: Efekt běží při každé změně dat studenta v cache — po tréninku, po návratu na kartu
  // i po refetchi z TD-466. Store a Sentry scope se proto aktualizují pokaždé, ale přesměrování
  // do onboardingu jen při PRVNÍM načtení. Obnovení na pozadí studenta uprostřed onboardingu
  // nesmí přehodit na jiný krok; při změně stránky ho hlídá efekt níž. [TD-472]
  useEffect(() => {
    if (userData?.currentUser === undefined) return;

    const currentUser = userData.currentUser;
    const isFirstLoad = !isFirstLoadHandledRef.current;
    isFirstLoadHandledRef.current = true;

    dispatch(setUser(currentUser));
    setSentryScope(currentUser);

    const timetableEntries = userData.timetableGenericAll as Array<{ created?: string | number | Date }>;
    const firstTimetableDate = [...timetableEntries].sort((a, b) =>
      String(a.created ?? "").localeCompare(String(b.created ?? "")),
    )[0]?.created;

    const firstDayForTimetable =
      typeof firstTimetableDate === "string" ? formatDate(new Date(firstTimetableDate)) : formatDate(new Date());

    dispatch(setTimetableCreatedDate(firstDayForTimetable));

    if (currentUser.onboarding.finished) {
      // AIDEV-NOTE: Studený start přímo na /kalendar: dotaz na dny kalendáře jde na server hned, souběžně
      // s vykreslením stránky, ne až z jejího efektu. Rozsah počítá `getCalendarRange` ze stejných hodnot,
      // jaké se tu právě dávají do store, takže stránka trefí tentýž cache klíč. Student bez plánu
      // (plán se teprve zakládá níž) a nedokončený onboarding (přesměruje se pryč) se přeskakují. [TD-476]
      if (
        isFirstLoad &&
        location.pathname.startsWith("/kalendar") &&
        typeof firstTimetableDate === "string" &&
        typeof currentUser.admissionsDeadline === "string"
      ) {
        prefetchCalendarData(dispatch, getCalendarRange(firstDayForTimetable, currentUser.admissionsDeadline));
      }

      // Cache studenta bez plánu zůstane prázdná, dokud ji nikdo neobnoví. Obnovení na pozadí
      // by za běhu první mutace založilo plán podruhé, proto jedna mutace naráz.
      if (
        typeof firstTimetableDate !== "string" &&
        typeof currentUser.admissionsDeadline === "string" &&
        !isCreatingTimetableRef.current
      ) {
        isCreatingTimetableRef.current = true;

        void setTimetableGenericMutation({
          from: firstDayForTimetable,
          to: currentUser.admissionsDeadline,
          timeTable: DEFAULT_GENERIC_TIMETABLE,
        })
          .catch(() => {
            // Error is handled by apiMutationError effect.
          })
          .finally(() => {
            isCreatingTimetableRef.current = false;
            setIsLoading(false);
          });
      } else if (!isCreatingTimetableRef.current) {
        setIsLoading(false);
      }

      return;
    }

    if (isFirstLoad) void navigate(getOnboardingPath(currentUser));

    setIsLoading(false);
  }, [userData]);

  // AIDEV-NOTE: Chunk kalendáře se při nečinnosti přednačítá jen tady, ne při startu aplikace: sem se
  // dostane pouze přihlášený student s dokončeným onboardingem, tedy ten, kdo kalendář opravdu otevře.
  // Nepřihlášení, rodiče, učitelé a student v onboardingu by ho stahovali zbytečně. Selhání přednačtení
  // nevyvolá reload stránky (viz `lazyWithPreload`). [TD-476]
  const isOnboardingFinished = userData?.currentUser?.onboarding.finished === true;
  useEffect(() => {
    if (!isOnboardingFinished) return;

    return preloadCalendarRouteWhenIdle();
  }, [isOnboardingFinished]);

  // AIDEV-NOTE: Efekt výš přesměruje jen při načtení dat studenta. Z rozdělaného onboardingu se
  // pak dalo dojít na nástěnku a trénovat bez plánu, takže jahoda z prvního tréninku přišla
  // vniveč. Hlídá se proto každá změna stránky — podle čerstvých dat, protože cache onboarding
  // dokončený mezitím (Ukončit v ukázce, výsledky ukázky) nezná. [TD-466]
  useEffect(() => {
    if (userData?.currentUser === undefined || userData.currentUser.onboarding.finished) return;
    if (location.pathname.startsWith("/onboarding")) return;

    const pathname = location.pathname;
    refetchUser()
      .then(({ data, isError }) => {
        // Po selhání RTK vrací poslední cache — podle ní se nerozhoduje, zkusí se při další změně stránky.
        if (isError) return;
        if (data?.currentUser === undefined || data.currentUser.onboarding.finished) return;
        // Odpověď dorazila, až když student odešel jinam — rozhoduje novější změna stránky.
        if (pathnameRef.current !== pathname) return;

        void navigate(getOnboardingPath(data.currentUser));
      })
      .catch(console.error);
  }, [location.pathname]);

  // AIDEV-NOTE: Návrat na kartu po delší pauze obnoví data studenta na pozadí (počítadla po půlnoci,
  // změny z jiného zařízení nebo ze Zóny). Stáří se bere z `fulfilledTimeStamp`, tedy z posledního
  // ÚSPĚŠNÉHO načtení; selhané obnovení ho neposouvá, takže další návrat to zkusí znovu.
  // Záměrně ne `refetchOnFocus` — to by obnovovalo při každém přepnutí karty. Běžící načtení RTK
  // Query samo nezdvojí (dotaz ve stavu pending se přeskočí). [TD-472]
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      const { userFulfilledTimeStamp, refetchUser } = userRefreshRef.current;
      if (userFulfilledTimeStamp === undefined) return;
      if (Date.now() - userFulfilledTimeStamp < USER_REFRESH_AFTER_MS) return;

      void refetchUser();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const setSentryScope = (user: UserType) => {
    const scope = Sentry.getCurrentScope();

    scope.setUser({
      email: user.email ?? undefined,
      name: user.name ?? undefined,
    });
  };

  const isNotOnboarding = !location.pathname.includes("onboarding");

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    // Spustí Firebase SDK listener pro foreground zprávy (app je otevřená).
    // Bez toho FCM zprávy doručené do foreground nikdo neposlouchá → tiché zahození.
    startForegroundMessaging();
    return stopForegroundMessaging;
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleSwMessage = (event: MessageEvent) => {
      if (event.data?.type !== "NOTIFICATION_CLICK" || typeof event.data.url !== "string") return;
      // URL z notifikace je absolutní – extrahuj pathname pro SPA navigaci bez page reload
      try {
        const { pathname, search, hash } = new URL(event.data.url as string);
        navigate(pathname + search + hash);
      } catch {
        navigate(event.data.url as string);
      }
    };

    navigator.serviceWorker.addEventListener("message", handleSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleSwMessage);
    };
  }, [navigate]);

  return (
    <>
      {userData !== undefined && !isLoading ? (
        <>
          {children}
          {isNotOnboarding && (
            <Suspense fallback={null}>
              <FeedbackForm />
            </Suspense>
          )}
          {isNotOnboarding && <PushNotificationOptIn />}
        </>
      ) : isUserError && userData === undefined ? (
        <div className={"fullpage bg-primary-subtle"}>
          <BgWaveTop className={"wave__top"} />

          <div className={"d-flex m-auto align-items-center gx-3"}>
            <h4 className={"mb-0"}>Nastala chyba během načítání dat uživatele!</h4>
          </div>
        </div>
      ) : isLoading ? (
        <div className={"fullpage bg-primary-subtle"}>
          <BgWaveTop className={"wave__top"} />

          <Loading />
        </div>
      ) : (
        <div className={"fullpage bg-primary-subtle"}>
          <BgWaveTop className={"wave__top"} />

          <div className={"d-flex m-auto align-items-center gx-3"}>
            <h4 className={"mb-0"}>Nepodařilo se načist data uživatele!</h4>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthenticatedArea;
